import { ArticleHeader, ShareArticle } from "components/article-components"
import {
  BlogAuthorInfo,
  BlogAdElement,
  BlogInfoBox,
  BlogTeaserText,
  ThematicLinkModule,
  VideoInBlog,
} from "components/blog-components"
import { Link, graphql } from "gatsby"
import {
  RecommendedArticlesWissen,
  RecommendedLexiconPosts,
} from "components/page-components/Wissen"
import { CustomConentBlockquote } from "components/blog-components/content-blocks"

import { Demosuche } from "components/page-components/Ausschreibungsdienste"
import { Helmet } from "react-helmet"
import Layout from "components/structure-components/layout"

import React from "react"
import { SEOComponent } from "components/seo-components"
import createStandartGermanDate from "scripts/createStandartGermanDate"
import generateMarkdownFaqData from "scripts/generateMarkdownFaqData"
import generateSchemaOrgFaq from "scripts/generateSchemaOrgFaq"
import { getSrc } from "gatsby-plugin-image"
import rehypeReact from "rehype-react"
import { MermaidAblauf } from "components/blog-components/mermaids"

const BlogPost = ({ location, data }) => {
  const { blogPost, defaultNewsThumbnail } = data
  const {
    additional_author_info,
    frontmatter,
    htmlAst,
    markdownAst_stringified,
    related_blog_posts,
    related_content,
    related_lexicon_posts,
    tags,
    thematic_content_linkmodule,
  } = blogPost

  console.log("thematic_content_linkmodule: ", thematic_content_linkmodule)

  const {
    description,
    excerpt,
    firstcreated,
    last_touched,
    slugline,
    teaser,
    thumbnail,
    thumbnail_large,
    title,
    video_url,
    video_title,
    video_duration,
    video_uploaddate,
  } = frontmatter

  const author = { name: frontmatter.author }

  const recommended_news_articles = related_content.filter((article) => {
    return !!article && article?.frontmatter?.category === "news"
  })

  console.log("htmlAst: ", htmlAst)

  const render_htmlAst = new rehypeReact({
    createElement: React.createElement,
    components: {
      h2: ({ children }) => <h2 className="title is-4">{children}</h2>,
      h3: ({ children }) => <h3 className="title is-5">{children}</h3>,
      videoinblog: VideoInBlog,
      mermaidablauf: MermaidAblauf,
      blogadelement: (props) => (
        <BlogAdElement {...{ target_group: "ausschreibungsdienste", props }} />
      ),
      bloglinkmodulethematic: () =>
        !!thematic_content_linkmodule?.text ? (
          <ThematicLinkModule {...{ ...thematic_content_linkmodule }} />
        ) : null,
      blockquote: ({ children }) => (
        <CustomConentBlockquote {...{ children }} />
      ),
      bloginfobox: BlogInfoBox,
      table: ({ children }) => (
        <div className="table-container">
          <table className="table">{children}</table>
        </div>
      ),
    },
  }).Compiler

  const blogPost_faq = generateMarkdownFaqData(
    JSON.parse(markdownAst_stringified)
  )
  const schema_org_faq = generateSchemaOrgFaq(blogPost_faq)

  const display_date = createStandartGermanDate(last_touched)

  const thumbnail_src = getSrc(thumbnail)

  const author_social_profiles = additional_author_info
    ? [additional_author_info?.linkedIn, additional_author_info?.xing].filter(
        (itm) => !!itm
      )
    : undefined

  const schema_org_data = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://bi-medien.de${location.pathname}`,
    },
    headline: title,
    description: description,
    image: [`https://bi-medien.de${thumbnail_src}`],
    datePublished: firstcreated,
    dateModified: last_touched,
    author: {
      "@type": author ? "Person" : "Organization",
      name: author?.name || "B_I MEDIEN",
      url: !!additional_author_info
        ? additional_author_info.website
        : undefined,
      sameAs: author_social_profiles,
    },
    publisher: {
      "@type": "Organization",
      name: "B_I MEDIEN",
      logo: {
        "@type": "ImageObject",
        url: `https://bi-medien.de/assets/b-i-medien-publisher-logo.png`,
      },
    },
  }

  if (video_url) {
    Object.assign(schema_org_data, {
      video: {
        "@type": "VideoObject",
        description: description,
        name: video_title,
        duration: `PT${video_duration?.split(":")[0]}M${
          video_duration?.split(":")[1]
        }S`,
        embedURL: `https://www.youtube.com/embed/${video_url?.split("=")[1]}`,
        thumbnailUrl: `https://bi-medien.de${thumbnail_src}`,
        uploadDate: video_uploaddate,
      },
    })
  }

  const breadcrumb_array = [
    {
      type: "BreadcrumbLink",
      content: `B_I MEDIEN`,
      link: `/`,
      react_key: "footer_breadcrumb_item_1",
      ressort: "mitternachtsblau",
    },
    {
      type: "BreadcrumbLink",
      content: `Vergabe-Wissen`,
      link: `/vergabe-wissen`,
      react_key: "footer_breadcrumb_item_2",
      ressort: "mitternachtsblau",
    },
    {
      type: "BreadcrumbLink",
      content: `Vergaberecht`,
      link: `/vergabe-wissen/blog`,
      react_key: "footer_breadcrumb_item_3",
      ressort: "mitternachtsblau",
    },
    {
      type: "BreadcrumbSpan",
      content: title,
      link: "",
      react_key: "footer_breadcrumb_item_4",
      ressort: "mitternachtsblau",
    },
  ]

  const open_graph_image = {
    url: `https://bi-medien.de${getSrc(thumbnail_large)}`,
    width: thumbnail_large?.childImageSharp?.gatsbyImageData?.width,
    height: thumbnail_large?.childImageSharp?.gatsbyImageData?.height,
    alt: title,
  }

  return (
    <Layout {...{ location, breadcrumb_array }}>
      <Helmet>
        <meta name="x-bi-article-type" content="blogpost" />
        {!!tags?.[0] ? (
          <meta
            name="x-bi-content-tags"
            content={JSON.stringify(
              tags?.map(({ short_title }) => short_title)
            )}
          />
        ) : null}
      </Helmet>
      <SEOComponent
        brand={`B_I MEDIEN`}
        title={title}
        description={description || excerpt}
        schema_org_data={schema_org_data}
        schema_org_faq={schema_org_faq}
        open_graph_image={open_graph_image}
      />

      <div className="columns is-centered">
        <div className="column is-half">
          <article className="section">
            <ArticleHeader
              {...{
                display_date,
                ressort: "mitternachtsblau",
                headline: title,
                is_h1: true,
                human_authors: [author],
              }}
            />
            <div className="content mt-5">
              <div className="blog-wrapper">
                {!!teaser ? <BlogTeaserText>{teaser}</BlogTeaserText> : null}
                {render_htmlAst(htmlAst)}
              </div>
            </div>
            {!!tags?.[0] ? (
              <React.Fragment>
                <p className="has-text-weight-bold has-text-centered mb-4">
                  Interessante Themen:
                </p>
                <div className="buttons is-centered">
                  {tags.map(({ tag, short_title }, i) => (
                    <Link
                      key={`blog-tag-link-${i}`}
                      className="button is-mitternachtsblau is-rounded is-outlined"
                      to={`/vergabe-wissen/thema/${tag}`}
                    >
                      {short_title}
                    </Link>
                  ))}
                </div>
              </React.Fragment>
            ) : null}
            <hr className="has-background-mitternachtsblau-30" />

            <div className="columns is-centered is-vcentered mt-4 ">
              <div className="column is-6 px-0 has-text-centered-mobile">
                <Link to={`/vergabe-wissen/blog`} className={`link`}>
                  Alle Beiträge
                </Link>
              </div>
              <div className="column is-6 px-0">
                <ShareArticle
                  {...{
                    description,
                    seo_title: title,
                    path: location.pathname,
                    ressort: "mitternachtsblau",
                    slugline,
                  }}
                />
              </div>
            </div>

            {!!additional_author_info ? (
              <BlogAuthorInfo {...{ author_info: additional_author_info }} />
            ) : null}
            {!!related_blog_posts[0] ? (
              <div className="section px-2">
                <RecommendedArticlesWissen
                  {...{
                    articles: related_blog_posts,
                    title,
                    defaultNewsThumbnail,
                  }}
                />
              </div>
            ) : null}
            {!!related_lexicon_posts[0] ? (
              <RecommendedLexiconPosts
                {...{
                  related_lexicon_posts,
                }}
              />
            ) : null}
            {recommended_news_articles?.[0] ? (
              <div className="section pt-6 px-2">
                <RecommendedArticlesWissen
                  {...{
                    articles: recommended_news_articles,
                    title,
                    defaultNewsThumbnail,
                  }}
                />
              </div>
            ) : null}
          </article>
        </div>
      </div>

      <div className="section has-background-mitternachtsblau-10">
        <div className="my-6">
          <Demosuche />
        </div>
      </div>
    </Layout>
  )
}

export default BlogPost

export const pageQuery = graphql`
  query ($id: String!) {
    blogPost: markdownRemark(id: { eq: $id }) {
      htmlAst
      markdownAst_stringified
      excerpt
      tags {
        tag
        short_title
      }
      additional_author_info {
        bio {
          data {
            childMarkdownRemark {
              html
            }
          }
        }
        email
        employer
        image {
          localFile {
            childImageSharp {
              gatsbyImageData(
                formats: [AUTO, AVIF]
                width: 160
                height: 160
                layout: CONSTRAINED
                placeholder: BLURRED
              )
            }
          }
        }
        linkedIn
        name
        position
        website
        xing
      }
      frontmatter {
        author
        description
        firstcreated
        last_touched
        slugline
        teaser
        thumbnail {
          childImageSharp {
            gatsbyImageData(
              formats: [AUTO, AVIF]
              width: 450
              height: 270
              layout: CONSTRAINED
              placeholder: BLURRED
            )
          }
        }
        thumbnail_large: thumbnail {
          childImageSharp {
            gatsbyImageData(
              formats: [AUTO]
              width: 600
              height: 335
              placeholder: BLURRED
              layout: CONSTRAINED
            )
          }
        }
        title
        video_url
        video_title
        video_duration
        video_uploaddate
      }
      related_content {
        excerpt
        frontmatter {
          slugline
          title
          category
          thumbnail {
            childImageSharp {
              gatsbyImageData(
                formats: [AUTO, AVIF]
                width: 544
                layout: CONSTRAINED
                placeholder: BLURRED
              )
            }
          }
        }
      }
      related_blog_posts {
        excerpt
        frontmatter {
          slugline
          title
          category
          thumbnail {
            childImageSharp {
              gatsbyImageData(
                formats: [AUTO, AVIF]
                width: 544
                layout: CONSTRAINED
                placeholder: BLURRED
              )
            }
          }
        }
      }
      related_lexicon_posts {
        excerpt
        frontmatter {
          slugline
          title
          category
        }
      }
      thematic_content_linkmodule {
        text
        title
        link
        image {
          localFile {
            childImageSharp {
              gatsbyImageData(
                formats: [AUTO, AVIF]
                width: 186
                layout: CONSTRAINED
                placeholder: BLURRED
              )
            }
          }
        }
      }
    }
    defaultNewsThumbnail: file(name: { eq: "default-news-thumbnail" }) {
      childImageSharp {
        gatsbyImageData(
          formats: [AUTO, AVIF]
          width: 544
          layout: CONSTRAINED
          placeholder: BLURRED
        )
      }
    }
  }
`
